import { useState, useCallback } from "react";
import { useItemsV2ControllerAutocomplete } from "@semo/api-contracts";
import { useDebounce } from "../../../../hooks";

/**
 * 공급사별 아이템 자동완성 훅
 *
 * 판매자 대시보드 견적 참여 시 사용
 * - 공급사의 과거 거래 내역 기반 단가 자동 제공
 * - 최소 2글자 이상 입력 시 검색
 * - Debounce 적용 (기본 300ms)
 *
 * @param supplierId - 공급사 ID (e.g., CMARKET_SUP001)
 * @param debounceMs - Debounce 지연 시간 (기본: 300ms)
 * @returns 자동완성 결과, 로딩 상태, 검색/초기화 함수
 *
 * @example
 * ```tsx
 * const { results, isLoading, search, clear } = useSupplierItemAutocomplete('CMARKET_SUP001');
 *
 * // 검색
 * search('복사용지');
 *
 * // 결과 사용
 * results.map(item => (
 *   <div key={item.id}>
 *     {item.name} - {item.supplierPrice || item.unitPrice}원
 *   </div>
 * ))
 * ```
 */
export const useSupplierItemAutocomplete = (
  supplierId: string,
  debounceMs = 300,
) => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, debounceMs);

  const { data, isLoading, error } = useItemsV2ControllerAutocomplete(
    {
      q: debouncedQuery,
      limit: 10,
      supplierId,
    },
    {
      query: {
        enabled: debouncedQuery.length >= 2,
        staleTime: 5 * 60 * 1000, // 5분
        gcTime: 10 * 60 * 1000, // 10분
      },
    },
  );

  const search = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  const clear = useCallback(() => {
    setQuery("");
  }, []);

  return {
    results: data?.data || [],
    isLoading,
    error,
    search,
    clear,
  };
};
